import React from 'react';
import FAQItem from 'components/common/FAQItem';
import FAQ from 'interface/FAQ';
import styles from 'styles/pages/FAQPage.module.sass';

/**
 * FAQ Item List
 */
const faqs: FAQ[] = [
  {
    question: "What kind of reports do you offer?",
    answer: "Zerone Analytiqs offers an extensive collection of reports and market insights sourced from various industries on the African continent. These include industry reports, consumer/market analytics, custom studies, and topical research focused on sectors like agriculture, agribusiness, retail, consumer goods, tourism, and hospitality. You can find comprehensive, data-driven analyses tailored to the realities and opportunities across Ghana, Nigeria, Kenya, South Africa, and beyond."
  },
  {
    question: "Who are these reports for?",
    answer: "These reports are designed for businesses, investors, researchers, policy-makers, and organizations seeking accurate, localized, and actionable data on African markets. Whether you are a corporation, a startup, an academic, or a change-maker looking to make informed decisions on the continent, Zerone Analytiqs reports are built to serve you."
  },
  {
    question: "How often are reports updated?",
    answer: "Reports are updated regularly to reflect ongoing trends, emerging data, and developments in target industries and countries. New categories, markets, and analytics are added as Zerone Analytiqs expands its coverage and platform features. Check the website for the latest releases and highlights."
  },
  {
    question: "Do I need an account to access reports?",
    answer: "Most reports and data offerings require an account to access or purchase. Creating an account also unlocks user features like saving reports to your workspace, exporting findings, and receiving updates on new content."
  },
  {
    question: "Can I access reports on mobile?",
    answer: "Yes. Zerone Analytiqs supports mobile access through the web platform (responsive on mobile browsers). This allows users to view, download, and interact with reports on the go."
  },
  {
    question: "How do I create an account?",
    answer: "To create an account, visit the Zerone Analytiqs website and click on the sign-up or registration prompt. You'll be guided to enter your details, and you may need to verify your email or phone number for security purposes."
  },
  {
    question: "What should I do if I forget my password?",
    answer: "If you forget your password, use the 'Forgot Password?' or 'Reset Password' link on the login page. Follow the instructions to receive a password reset link via your registered email or phone."
  },
  {
    question: "What's the difference between basic and premium reports?",
    answer: "Basic Reports typically offer summary insights, select findings, or 'light' versions of market research, and may be available for free or at a lower cost. Premium Reports provide in-depth analysis, comprehensive data sets, advanced analytics, sector breakdowns, and exclusive insights, often requiring subscription or one-time purchase for full access."
  },
  {
    question: "How much do reports cost?",
    answer: "Report pricing varies based on depth, industry, and access rights. Zerone Analytiqs offers on-demand pricing, institution/education pricing, and premium pricing for full-featured reports. For current pricing, visit the Pricing section on the website or contact the team directly for quotes on custom or enterprise solutions."
  },
  {
    question: "In what formats are the reports delivered?",
    answer: "Reports can be delivered and exported as Microsoft Excel, PowerPoint, and PDF files. This makes it easy to integrate Zerone insights into your presentations, analyses, and decision workflows."
  },
  {
    question: "Can I download reports?",
    answer: "Yes. After purchasing or unlocking a report, you can download it directly in your preferred format (Excel, PowerPoint, PDF) for offline use and sharing."
  },
  {
    question: "How can I contact the Zerone Analytics team?",
    answer: "You can reach Zerone Analytiqs via their official contact page at https://zeroneanalytiqs.com/contact-us or use the contact information listed there to email or message the team for support, sales, or media inquiries."
  },
  {
    question: "How do I report an issue with a report or purchase?",
    answer: "If you encounter any issue with a report or your purchase, contact support via the official website's contact page or use the help/request/feedback options in your user account or app. The team will assist you in resolving your issue promptly."
  },
  {
    question: "Are there any free reports available?",
    answer: "Occasionally, Zerone Analytiqs provides free access to select reports or summary insights—especially for new users, special topics, or through its blog and highlights. Monitor the main website, report categories, and blog for free downloads or sample content."
  }
];

/**
 * FAQText component - Renders a list of frequently asked questions (FAQs) using the FAQItem component.
 *
 * @component
 * @example
 * // Example usage:
 * import FAQText from 'components/layout/faq/FAQText';
 * 
 * const App: React.FC = () => (
 *   <div>
 *     <h1>Frequently Asked Questions</h1>
 *     <FAQText />
 *   </div>
 * );
 **/
const FAQText: React.FC = () => (
  <div className={styles['faq-content-section']}>
    {faqs.map((faq, idx) => (
      <FAQItem key={idx} faq={faq} />
    ))}
  </div>
);

export default FAQText;
